import { useCallback } from "react";

import type { IssueRow } from "#app/lib/data";
import { useWorkspaceData } from "#app/lib/data/workspace-data-provider";
import { toastMutationError } from "#app/lib/utils/toast-errors";
import {
  type IssueEditTracking,
  useIssueViewMutations,
} from "#app/hooks/issue-view/use-issue-view-mutations";

/**
 * The Teams column's write: the picker reports the full id set per change,
 * so the Teams property is replaced wholesale. Optimistic — the cell's chips
 * update in place from the workspace team map. Every issue table's Teams
 * column (the Projects list, a widget's Data tab) commits through this one.
 */
export function useIssueTeamsChange(
  tracking: IssueEditTracking = {},
): (issue: IssueRow, teamIds: string[]) => void {
  const { teamMap } = useWorkspaceData();
  const { updateTeams } = useIssueViewMutations(tracking);
  return useCallback(
    (issue: IssueRow, teamIds: string[]) => {
      updateTeams({
        variables: { issueId: issue.issueId, teamIds },
        optimisticResponse: {
          updateIssue: {
            __typename: "Issue" as const,
            id: issue.id,
            issueId: issue.issueId,
            teams: teamIds.map((teamId) => {
              const team = teamMap.get(teamId);
              const kept = issue.teams?.find((t) => t.teamId === teamId);
              return {
                __typename: "Team" as const,
                id: teamId,
                teamId,
                teamName: team?.teamName ?? kept?.teamName ?? "?",
                icon: {
                  __typename: "Icon" as const,
                  icon: team?.icon?.icon ?? kept?.icon?.icon ?? null,
                  color: team?.icon?.color ?? kept?.icon?.color ?? null,
                  percentage: null,
                },
              };
            }),
          },
        },
        onError: (err) => toastMutationError(err, "Failed to update teams"),
      });
    },
    [updateTeams, teamMap],
  );
}
